import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';

import { fetchJson } from '@/lib/queryClient';

interface PassOfferResponse {
  offer: {
    id: string;
    name: string;
    sessionCount: number;
    priceCents: number;
    isActive: boolean;
    classes: Array<{ id: string; title: string }>;
  } | null;
}

/**
 * Wraps a class form's punch-pass fields. When the class is on a shared pass
 * (one pass product covering several classes), those fields don't apply — the
 * server ignores them — so show which pass it's on instead. Otherwise renders
 * the fields unchanged.
 */
export function SharedPassGate({
  classId,
  children,
}: {
  classId: string | null | undefined;
  children: ReactNode;
}) {
  const { data } = useQuery<PassOfferResponse>({
    queryKey: ['/api/classes', classId, 'pass-offer'],
    queryFn: () => fetchJson<PassOfferResponse>(`/api/classes/${classId}/pass-offer`),
    enabled: !!classId,
  });
  const offer = data?.offer;

  if (!classId || !offer || offer.classes.length < 2) return <>{children}</>;

  return (
    <div className="space-y-1 rounded-md border p-3 text-sm" data-testid="shared-pass-notice">
      <p className="font-medium">Punch pass: {offer.name}</p>
      <p className="text-muted-foreground">
        {offer.sessionCount} sessions · ${(offer.priceCents / 100).toFixed(2)}
        {offer.isActive ? '' : ' · not on sale'}
      </p>
      <p className="text-muted-foreground">
        Shared with{' '}
        {offer.classes
          .filter(c => c.id !== classId)
          .map(c => c.title)
          .join(', ')}
        . Contact Kalovate to change this pass.
      </p>
    </div>
  );
}
